// aiResponseFilter — Scanna AI-output för läckor av tools, system-prompt,
// eller provider/model-identitet. Output-guard som komplement till
// request-time aiExfiltrationGuard.
//
// Filosofi: om modellen *ändå* börjar svara med "my system prompt says…"
// eller "I have access to these tools: X, Y, Z" så byter vi ut svaret
// mot en standard-refusal och loggar incident:en för audit.

export type LeakFlag =
  | 'system_prompt_leak'
  | 'tool_list_leak'
  | 'model_identity_leak'
  | 'encoded_payload';

export interface FilterResult {
  safe: boolean;
  flags: LeakFlag[];
  /** Säker text som ska levereras till user. Vid leak: refusal-meddelande. */
  sanitized: string;
  /** Ursprunglig text, behålls för audit (aldrig till user). */
  original: string;
}

const LEAK_PATTERNS: Array<{ flag: LeakFlag; regex: RegExp }> = [
  // "my system prompt says", "I was instructed to", "according to my instructions"
  {
    flag: 'system_prompt_leak',
    regex: /\b(my|the)\s+(system\s+)?(prompt|instructions?|rules?)\s+(says?|stated?|tells?\s+me|instructs?)|\bi\s+was\s+(instructed|told|programmed)\s+to\b|\baccording\s+to\s+my\s+(instructions?|prompt|rules?)\b/i,
  },
  // "I have access to the following tools:" etc.
  {
    flag: 'tool_list_leak',
    regex: /\bi\s+(have\s+access\s+to|can\s+use|am\s+equipped\s+with)\s+(?:the\s+)?(following\s+)?(tools?|functions?|commands?)\b|\b(?:my\s+)?available\s+(tools?|functions?|capabilities)\s+(are|include|consist)\b/i,
  },
  // "I am powered by", "I use GPT-4", "I am Claude/Gemini/GPT"
  {
    flag: 'model_identity_leak',
    regex: /\b(i\s+am|this\s+is)\s+(claude|gpt-?\d|gemini|llama|mistral|gemma|openai|anthropic|google\s+ai)\b|\bi\s+(am\s+)?(powered\s+by|run\s+on|built\s+(on|with)|using)\s+(claude|gpt|gemini|llama|openai|anthropic)/i,
  },
  // Base64-ish string >= 40 chars (heuristic för hidden tools/prompts).
  // Använder contiguous [A-Za-z0-9+/] — tar inte med '=' i räkningen, så
  // gränsen är effektivt 40+ base64-chars.
  {
    flag: 'encoded_payload',
    regex: /(?:^|[^A-Za-z0-9+/])[A-Za-z0-9+/]{40,}={0,2}/,
  },
];

export const LEAK_REFUSAL_MESSAGE =
  "I'm not able to share that. Let me know what I can help you with regarding your task.";

export function filterResponse(text: string): FilterResult {
  if (!text) {
    return { safe: true, flags: [], sanitized: '', original: '' };
  }

  const flags: LeakFlag[] = [];
  for (const { flag, regex } of LEAK_PATTERNS) {
    if (regex.test(text)) flags.push(flag);
  }

  if (flags.length === 0) {
    return { safe: true, flags: [], sanitized: text, original: text };
  }

  return {
    safe: false,
    flags,
    sanitized: LEAK_REFUSAL_MESSAGE,
    original: text,
  };
}

/** Filter streaming chunks — när vi ser en chunk som innehåller läck-mönster
 * ska vi avbryta streamen och leverera refusal istället. Returnerar true
 * om chunken passar, false om den ska trigga abort. */
export function isChunkSafe(chunk: string): boolean {
  if (!chunk) return true;
  for (const { regex } of LEAK_PATTERNS) {
    if (regex.test(chunk)) return false;
  }
  return true;
}

/**
 * S-129: cross-chunk-aware variant av `isChunkSafe`. SSE-stream:en levererar
 * 5-30 char per delta — frasen `\bi\s+am\s+claude\b` matchar nästan ALDRIG
 * en enskild delta (`I am Cl` + `aude` levereras som två separata chunks).
 *
 * Lösning: matcha mönster mot ett sliding-window som koncat:ar den sista
 * `windowChars`-delen av redan-mottagen text med den nya delta:n. Window
 * är generöst tilltaget (default 200 chars) så även de längsta mönstren
 * får tillräcklig kontext utan O(n²)-regex på hela `assistantSoFar`.
 *
 * Returnerar true om kombinationen är säker, false om läck detekteras.
 *
 * Användning i runtime-stream-läsare:
 *   if (!isStreamSafe(assistantSoFar, delta)) {
 *     // avsluta streamen + sätt LEAK_REFUSAL_MESSAGE
 *   }
 */
export const STREAM_LEAK_WINDOW_CHARS = 200;

export function isStreamSafe(
  prevBuffer: string,
  newDelta: string,
  windowChars: number = STREAM_LEAK_WINDOW_CHARS,
): boolean {
  if (!newDelta) return true;
  // Bara sista `windowChars`-tecken av prev-buffer + nya delta:n.
  // Om prev-buffer är kort koncat:as bara hela.
  const tail =
    prevBuffer.length > windowChars
      ? prevBuffer.slice(prevBuffer.length - windowChars)
      : prevBuffer;
  const window = tail + newDelta;
  for (const { regex } of LEAK_PATTERNS) {
    if (regex.test(window)) return false;
  }
  return true;
}

export function leakSummary(result: FilterResult): string {
  if (result.safe) return 'safe';
  return `leak:${result.flags.join(',')}`;
}
